<template>
  <Transition name="grow-list">
    <aside
      v-if="visibilityStore.showZoomCard"
      id="componentZoomCard"
      v-trap-focus
      :aria-label="t('zoomSelector.componentTitle')"
      class="zoom-card h-auto justify-center items-center"
      :class="{
        ['zoom-card--floating']: floatingMode,
        ['zoom-card--vertical']: verticalMode,
      }"
      @keydown.esc="visibilityStore.setShowZoomCard(false)"
    >
      <span
        id="anchorFocusZoomCardComponent"
        tabindex="0"
        aria-hidden="true"
      ></span>

      <ul
        :aria-label="t('zoomSelector.listName')"
        class="flex flex-col self-start"
      >
        <li
          v-for="option in options"
          :key="option.translationKey"
          v-focus
          :title="option.title"
          :class="{ ['zoom-option--active']: option.active }"
          @click.prevent="handleClick(option)"
        >
          <button
            class="button-text w-full border-b px-2 py-1 hover:bg-gray-50 hover:text-gray-800 cursor-pointer"
          >
            {{ option.title }}
          </button>
        </li>
      </ul>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { trapFocus } from "@/helpers/trapFocus";
import { zoomOptions } from "@/helpers/constants";
import { useSettingsStore } from "@/stores/settings";
import { useUiVisibilityStore } from "@/stores/uiVisibility";

import { useWpsClient } from "@/wps/client";

const { t } = useI18n();

const wpsClient = useWpsClient();

const settingsStore = useSettingsStore();
const visibilityStore = useUiVisibilityStore();

const props = defineProps<{
  floatingMode: boolean;
  verticalMode: boolean;
}>();

const options = computed<
  {
    value: string;
    active: boolean;
    title: string;
    translationKey: string;
  }[]
>(() => {
  const activeZoom = settingsStore.perContent.customZoomPDF;

  const sourceOptions = props.verticalMode
    ? zoomOptions.scrolling
    : wpsClient.media.isFXL
      ? zoomOptions.fxl
      : zoomOptions.paginated;

  return sourceOptions.map((option) => {
    return {
      value: option.value,
      active: activeZoom === option.value,
      title: isNaN(parseInt(option.value))
        ? t(`zoomSelector.${option.translationKey}`)
        : `${option.value}%`,
      translationKey: option.translationKey,
    };
  });
});

function handleClick(option: { value: string; translationKey: string }) {
  settingsStore.perContent.customZoomPDF =
    option.value as typeof settingsStore.perContent.customZoomPDF;
  visibilityStore.setShowZoomCard(false);
}

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
    const anchorFocus = document.getElementById("anchorFocusZoomCardComponent");
    anchorFocus?.focus();
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

const vFocus = {
  mounted: (el: HTMLElement) => {
    if (el?.classList?.contains("zoom-option--active")) {
      requestAnimationFrame(() => {
        el.scrollIntoView();
      });
    }
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = document.getElementById("componentZoomCard");

  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.zoom-card {
  width: 275px;
  z-index: 3;
  max-height: 300px;
  overflow-y: auto;
  padding: $spacingM;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  border: 3px solid rgba(158, 158, 158, 0.2);
  @include scrollBar();

  &--floating {
    position: absolute;
    bottom: $barHeight;
    box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
    right: $spacingM;
  }

  &--vertical {
    bottom: calc($barHeight - 18px);
  }
}

.button-text {
  border-color: var(--DM__theme-separator-color);
}

.custom-zoom {
  width: 100%;
  padding: $spacingS $spacingM;
  margin: $spacingS 0 $spacingL 0;
  border-top: solid $spacingXS var(--DM__theme-hover-color);
  border-bottom: solid $spacingXS var(--DM__theme-hover-color);
  background-color: var(--DM__theme-hover-color);
  color: var(--DM__theme-font-color);
  @media (any-hover: hover) {
    &:hover,
    &:focus,
    &:active {
      outline: 0;
      background-color: var(--DM__theme-hover-color);
    }

    &:focus {
      border-color: var(--DM__theme-border-color);
    }

    &:focus:not(:focus-visible) {
      outline: 0;
      box-shadow: none;
      border-color: var(--DM__theme-border-color);
    }
  }
}

.zoom-option {
  &--active {
    .button-text {
      text-decoration: underline;
      text-decoration-color: var(--DM__theme-slider-color);
      text-underline-offset: 3px;
      text-decoration-thickness: 2px;
    }
  }
}
</style>
